import { create } from "zustand";

export interface ProgressEntry {
  id: string;
  type: "analysis" | "research_assistant" | "comparison";
  label: string;
  startedAt: number;
  /**
   * The project this operation belongs to. The bar is hidden on that
   * project's Results tab because inline indicators already cover it;
   * it renders everywhere else (other tabs, other projects, dashboard
   * list) so long-running work stays visible when the user navigates
   * away.
   */
  projectId: string;
  /**
   * URL to navigate to when the user clicks the bar row — typically the
   * Results tab of the owning project. Captured at publish time so the
   * click handler doesn't need to reconstruct org/workspace/project IDs.
   */
  returnUrl: string;
  progress?: { step: string; completed: number; total: number };
}

type State = {
  entries: ProgressEntry[];
};

type Action = {
  addEntry: (entry: ProgressEntry) => void;
  updateEntry: (
    id: string,
    updates: Partial<Omit<ProgressEntry, "id">>,
  ) => void;
  removeEntry: (id: string) => void;
};

const useGlobalProgressStore = create<State & Action>((set) => ({
  entries: [],
  addEntry: (entry) =>
    set((state) => ({
      entries: [
        // Replace if same id already exists (idempotent)
        ...state.entries.filter((e) => e.id !== entry.id),
        entry,
      ],
    })),
  updateEntry: (id, updates) =>
    set((state) => ({
      entries: state.entries.map((e) =>
        e.id === id ? { ...e, ...updates } : e,
      ),
    })),
  removeEntry: (id) =>
    set((state) => ({
      entries: state.entries.filter((e) => e.id !== id),
    })),
}));

export default useGlobalProgressStore;
